import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60 seconds default
});

// Extended timeout API for AI requests (5 minutes)
export const aiApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minutes for AI responses
});

// Request interceptor to add auth token
const addAuthToken = (config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

api.interceptors.request.use(addAuthToken, (error) => Promise.reject(error));
aiApi.interceptors.request.use(addAuthToken, (error) => Promise.reject(error));

// Custom error class that preserves response data
export class ApiError extends Error {
  response?: AxiosError['response'];

  constructor(message: string, response?: AxiosError['response']) {
    super(message);
    this.name = 'ApiError';
    this.response = response;
  }
}

// Response interceptor for error handling
const handleResponseError = (error: AxiosError<{ message?: string; error?: string; session?: unknown }>) => {
  if (error.response?.status === 401) {
    localStorage.removeItem('token');
    window.location.href = '/login';
  }

  const message =
    error.response?.data?.message ||
    error.response?.data?.error ||
    error.message ||
    'An unexpected error occurred';

  // Preserve the response data for handlers that need it (e.g., session recovery)
  return Promise.reject(new ApiError(message, error.response));
};

api.interceptors.response.use((response) => response, handleResponseError);
aiApi.interceptors.response.use((response) => response, handleResponseError);

export default api;
